import { useState, useCallback, useEffect, useRef } from 'react';
import ManualSearch from './ManualSearch';
import { useManualNavigation } from '../hooks/useManualNavigation';
import { NavigationSection } from './NavigationSection';
import { LoadingState } from './LoadingState';

interface ManualLayoutProps {
  children: React.ReactNode;
  currentPath?: string;
  language: 'ja' | 'en';
  onLanguageChange: (lang: 'ja' | 'en') => void;
}

export default function ManualLayout({ children, currentPath, language, onLanguageChange }: ManualLayoutProps) {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const { sections, loading } = useManualNavigation(language);
  const contentRef = useRef<HTMLElement>(null);

  // ページ遷移時にコンテンツエリアのスクロールをリセット
  useEffect(() => {
    contentRef.current?.scrollTo(0, 0);
  }, [currentPath]);

  // Auto-expand the section containing the current article
  useEffect(() => {
    if (!currentPath || !currentPath.startsWith('/manual/article/')) return;
    // Extract section from /manual/article/section/slug
    const match = currentPath.match(/^\/manual\/article\/([a-z0-9-]+)\//);
    if (match) {
      const sectionId = match[1];
      setExpandedSections((prev) => {
        if (prev.has(sectionId)) return prev;
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
    }
  }, [currentPath]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(sectionId)) {
        newExpanded.delete(sectionId);
      } else {
        newExpanded.add(sectionId);
      }
      return newExpanded;
    });
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      window.location.hash = path.startsWith('#') ? path : `#${path}`;
      setIsNavOpen(false);
    },
    []
  );

  return (
    <div className="manual-layout">
      <header className="manual-header">
        <div className="manual-header-content">
          <button
            className="manual-mobile-menu-toggle"
            onClick={() => setIsNavOpen(!isNavOpen)}
            aria-label={language === 'en' ? 'Open menu' : 'メニューを開く'}
          >
            ☰
          </button>

          <img
            src="/exworks-dark.png"
            alt="ExWorks"
            className="manual-header-logo"
            style={{ cursor: 'pointer' }}
            onClick={() => { window.location.hash = '#/manual'; }}
          />
          <p className="manual-header-title">Trading Playground</p>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0' }}>
            <button
              onClick={() => onLanguageChange('ja')}
              style={{
                padding: '3px 10px',
                fontSize: '12px',
                fontWeight: 600,
                border: language === 'ja' ? '1px solid var(--ink)' : '1px solid var(--line)',
                borderRadius: '4px 0 0 4px',
                cursor: 'pointer',
                backgroundColor: language === 'ja' ? 'var(--ink)' : 'transparent',
                color: language === 'ja' ? 'var(--bg)' : 'var(--muted)',
                transition: 'all 0.2s',
              }}
            >
              JA
            </button>
            <button
              onClick={() => onLanguageChange('en')}
              style={{
                padding: '3px 10px',
                fontSize: '12px',
                fontWeight: 600,
                border: language === 'en' ? '1px solid var(--ink)' : '1px solid var(--line)',
                borderRadius: '0 4px 4px 0',
                cursor: 'pointer',
                backgroundColor: language === 'en' ? 'var(--ink)' : 'transparent',
                color: language === 'en' ? 'var(--bg)' : 'var(--muted)',
                transition: 'all 0.2s',
              }}
            >
              EN
            </button>
          </div>
        </div>
      </header>

      <div className="manual-body">
        <aside className={`manual-sidebar ${isNavOpen ? 'open' : ''}`}>
          <div className="manual-sidebar-header">
            <button
              className="manual-sidebar-close"
              onClick={() => setIsNavOpen(false)}
              aria-label={language === 'en' ? 'Close menu' : 'メニューを閉じる'}
            >
              ✕
            </button>
          </div>

          <ManualSearch onNavigate={handleNavigate} language={language} />

          <nav className="manual-nav">
            {loading ? (
              <LoadingState />
            ) : (
              sections.map((section) => (
                <NavigationSection
                  key={section.id}
                  section={section}
                  isExpanded={expandedSections.has(section.id)}
                  currentPath={currentPath}
                  onToggle={() => toggleSection(section.id)}
                  onNavigate={handleNavigate}
                />
              ))
            )}
          </nav>
        </aside>

        <main className="manual-content" ref={contentRef}>{children}</main>
      </div>

      {isNavOpen && (
        <div
          className="manual-overlay"
          onClick={() => setIsNavOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
